import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import { nullableImageFromCMSSchema, richTextFromCMSSchema } from '../../cms';
import { recurringOrdersProps } from '../constants';

const headers = {
  object: {
    headerShippingAddress: yup.string().required(),
    headerAccountInfo: yup.string().required(),
    headerIDMeForm: yup.string().required(),
    headlineBillingInformation: yup.string().required(),
  },
  transform: value => ({
    headerShippingAddress: value.header_shipping_address,
    headerAccountInfo: value.header_account_info,
    headerIDMeForm: value.header_idme,
    headlineBillingInformation: value.header_billing_info,
  }),
};

const stateOptions = {
  object: {
    stateOptions: yup.array(),
  },
  transform: value => ({
    stateOptions: value.map(({ label, value }) => ({
      label,
      value,
    })),
  }),
};

const shippingAddressForm = {
  object: {
    labelFirstName: yup.string().required(),
    labelLastName: yup.string().required(),
    labelAddress: yup.string().required(),
    labelApt: yup.string().required(),
    labelZipCode: yup.string().required(),
    labelCity: yup.string().required(),
    labelState: yup.string().required(),
    stateOptions: yup.array().required(),
    labelPhoneNumber: yup.string().required(),
    labelSMSUpdates: yup.string().required(),
    labelSMSTerms: yup.string().required(),
  },
  transform: value => ({
    labelFirstName: value.shipping_address[0].label_first_name,
    labelLastName: value.shipping_address[0].label_last_name,
    labelAddress: value.shipping_address[0].label_address,
    labelApt: value.shipping_address[0].label_apt,
    labelZipCode: value.shipping_address[0].label_zip_code,
    labelCity: value.shipping_address[0].label_city,
    labelState: value.shipping_address[0].label_state,
    ...stateOptions.transform(value.shipping_address[0].state_options),
    labelPhoneNumber: value.shipping_address[0].label_phone,
    labelSMSUpdates: value.shipping_address[0].label_sms_updates,
    labelSMSTerms: value.shipping_address[0].label_sms_terms,
  }),
};

const accountInformationForm = {
  object: {
    labelEmail: yup.string().required(),
    labelPassword: yup.string().required(),
    labelConfirmation: yup.string().required(),
  },
  transform: value => ({
    labelEmail: value.account_info[0].label_email,
    labelPassword: value.account_info[0].label_password,
    labelConfirmation: value.account_info[0].label_password_confirmation,
  }),
};

const billingInformationForm = {
  object: {
    labelWithStripe: yup.string(),
    labelNameCard: yup.string().required(),
    labelCardNumber: yup.string().required(),
    labelExpirationDate: yup.string().required(),
    labelCVC: yup.string().required(),
  },
  transform: value => ({
    labelWithStripe: 'Pay securely with',
    labelNameCard: value.label_name_card,
    labelCardNumber: value.label_card_number,
    labelExpirationDate: value.label_expiration_date,
    labelCVC: value.label_cvc,
    placeholderNameCard: value.label_name_card,
    placeholderCardNumber: '1234 1234 1234 1234',
    placeholderExpirationDate: 'MM/YY',
    placeholderCVC: value.label_cvc,
  }),
};

const idMeForm = {
  object: {
    message: richTextFromCMSSchema,
    buttonImage: yup.string().required(),
    buttonAlt: yup.string().required(),
  },
  transform: value => ({
    message: value.message,
    buttonAlt: value.image.name,
    buttonImage: value.image.filename,
  }),
};

const moneyBack = {
  object: {
    headline: yup.string().required(),
    description: yup.string().required(),
  },
  transform: value => ({
    headline: value.headline,
    description: value.description,
  }),
};

const checkoutBar = {
  object: {
    ctaText: yup.string().required(),
  },
  transform: value => ({
    ctaText: 'Checkout',
  }),
};

const planSummary = {
  object: {
    headline: yup.string().required(),
    numPacksCopy: yup.string().required(),
    recipesLabel: yup.string().required(),
    image: nullableImageFromCMSSchema,
  },
  transform: value => ({
    headline: value.headline,
    numPacksCopy: value.label_num_packs,
    image: value.image,
    recipesLabel: 'Recipes',
  }),
};

const pricingList = yup
  .object({
    totalPlanLabel: yup.string().required(),
    puptainerLabel: yup.string().required(),
    shippingLabel: yup.string().required(),
    scooperLabel: yup.string().required(),
    bowlLabel: yup.string(),
    foodSubtotalLabel: yup.string().required(),
    addonsSubtotalLabel: yup.string().required(),
    discountLabel: yup.string().required(),
    taxLabel: yup.string().required(),
    totalLabel: yup.string().required(),
    promoCodeInfo: yup.object({
      promoCodeLabel: yup.string(),
      applyPromoCodeLabel: yup.string(),
      promoCodeAppliedLabel: yup.string(),
      removePromoCodeLabel: yup.string(),
      errorPromoCodeLabel: yup.string(),
      changePromoCodeLabel: yup.string(),
      newPromoCodeLabel: yup.string(),
    }),
    freeItems: yup.array(
      yup
        .object({
          label: yup.string(),
          quantity: yup.string().nullable(),
          gwp: yup.bool(),
          image: nullableImageFromCMSSchema,
          backgroundColor: yup.string().nullable(),
          fontColor: yup.string().nullable(),
        })
        .transform(value => ({
          label: value.label,
          quantity: value.quantity || null,
          gwp: value.gwp,
          image: value.image,
          backgroundColor: value.backgroundColor,
          fontColor: value.fontColor,
        })),
    ),
  })
  .transform(value => {
    let { value: experimentValue } = useExperiment(
      value.free_items_list?.[0]?.test_name ?? null,
    );
    experimentValue = experimentValue || 'control';
    let freeItemsList = value.free_items_list;
    if (freeItemsList?.[0]?.component === 'flagship_ab_test') {
      const experimentVariation = freeItemsList[0].variants.find(
        variant => variant.variant_name === experimentValue,
      );
      freeItemsList = experimentVariation?.blocks ?? [];
    }
    return {
      totalPlanLabel: value.label_total_plan,
      puptainerLabel: value.label_puptainer,
      shippingLabel: value.label_shipping,
      scooperLabel: value.label_scooper,
      bowlLabel: value.bowl_label,
      foodSubtotalLabel: value.label_food_subtotal,
      addonsSubtotalLabel: value.label_addons_subtotal,
      discountLabel: value.label_discount,
      taxLabel: value.label_tax,
      totalLabel: value.label_total,
      promoCodeInfo: {
        promoCodeLabel: value.label_promo_code_default,
        applyPromoCodeLabel: value.apply_code_button_text,
        promoCodeAppliedLabel: value.label_promo_code_applied,
        removePromoCodeLabel: value.remove_code_button_text,
        errorPromoCodeLabel: value.label_error_promo_code,
        changePromoCodeLabel: value.change_code_button_text,
        newPromoCodeLabel: value.label_new_promo_code,
      },
      freeItems: freeItemsList || value.free_items_list,
    };
  });

const orderSummary = {
  object: {
    termsAndConditions: yup.string().required(),
    checkoutButton: yup
      .object({
        text: yup.string().required(),
      })
      .required()
      .transform(value => ({
        text: value.checkout_button_text,
      })),

    planSummary: yup
      .object(planSummary.object)
      .required()
      .transform(planSummary.transform),

    pricingList: pricingList.required(),
  },
  transform: value => ({
    addPupBtnLabel: 'Add another pup',
    termsAndConditions:
      '* By clicking checkout, I agree to receive the number of packs of Ollie food on the schedule specified above. I agree to be automatically charged for each shipment to my payment method on file. I can change or cancel my subscription at any time by emailing',
    pricingList: value.pricing_list[0],
    planSummary: value.starter_box_checkout_summary[0],
    checkoutButton: value,
  }),
};

const recurringOrders = {
  object: {
    foodPacksText: yup.string().required(),
  },
  transform: value => ({
    foodPacksText: recurringOrdersProps.foodPacksText,
  }),
};

const links = {
  object: {
    links: yup.array(),
  },
  transform: value => ({
    links: value.map(link => ({
      href: link.link_target.url,
      text: link.link_text,
    })),
  }),
};

export const checkoutSchema = yup
  .object({
    ...headers.object,
    ...shippingAddressForm.object,
    ...accountInformationForm.object,
    ...billingInformationForm.object,
    ...moneyBack.object,
    ...idMeForm.object,
    ...checkoutBar.object,
    ...links.object,
    ...stateOptions.object,
    orderSummary: yup
      .object(orderSummary.object)
      .required()
      .transform(orderSummary.transform),

    recurringOrders: yup
      .object(recurringOrders.object)
      .required()
      .transform(recurringOrders.transform),
  })
  .transform(value => ({
    ...headers.transform(value),
    ...shippingAddressForm.transform(value),
    ...accountInformationForm.transform(value),
    ...billingInformationForm.transform(value.billing_info[0]),
    ...moneyBack.transform(value.billing_info[0].money_back[0]),
    ...idMeForm.transform(value.id_me[0]),
    ...checkoutBar.transform(value.bar[0]),
    ...links.transform(value.links),
    orderSummary: value.summary[0],
    recurringOrders: value.recurring_orders[0],
  }));
